import React from 'react';
import BlogLayout from '../../components/BlogLayout';
import { Callout, Checklist, StepCardList, Takeaway, VideoEmbed } from '../../components/blog/ArticlePrimitives';

const SITE = 'https://www.roofclaw.com';
const SLUG = '/blog/meta-ads-mcp-higgsfield-openclaw';
const repoUrl = 'https://github.com/Roofing-Business-Partner/Roofclaw-MetaAds-Agent';

const FAQ_ITEMS = [
  {
    q: 'What is the RoofClaw Meta Ads Agent?',
    a: 'The RoofClaw Meta Ads Agent is an open-source launchpad for turning a MARKETING-configured RoofClaw into an AI-assisted Meta Ads production agent. It combines OpenClaw, Higgsfield MCP, Meta Ads MCP, the official Meta Ads CLI, and a safety-first workflow where campaigns are built paused until a human approves them.',
  },
  {
    q: 'Why does this matter for roofers?',
    a: 'Most roofers do not have a repeatable creative department. This workflow gives them a repeatable way to move from offer, story, storyboard, AI-generated assets, and video into a real Meta campaign without hiring an agency for every concept.',
  },
  {
    q: 'What is Higgsfield MCP used for in this workflow?',
    a: 'Higgsfield MCP is used for creative asset production: character references, image generation, Soul ID identity work, Seedance video generation, and commercial-style visuals. The key lesson is model selection discipline: wide environment shots, character closeups, product ads, and dramatic portraits need different models.',
  },
  {
    q: 'What is Meta Ads MCP used for?',
    a: 'Meta Ads MCP is best used as the conversational inspection and reasoning layer. An OpenClaw agent can use it to understand account context, campaigns, pages, and opportunities in natural language before switching to deterministic CLI or API calls for execution.',
  },
  {
    q: 'Why use the Meta Ads CLI if the MCP exists?',
    a: 'The CLI gives repeatable, scriptable execution. It is better for creating campaigns, listing ad sets, capturing logs, and building repeatable launch procedures. MCP is excellent for context; CLI is better for deterministic operations.',
  },
  {
    q: 'Can the agent spend ad budget automatically?',
    a: 'No. The launchpad is designed around a PAUSED-by-default rule. Campaigns, ad sets, creatives, and ads should be created in paused state until a human reviews the creative, budget, targeting, and compliance posture.',
  },
  {
    q: 'What was the biggest Higgsfield lesson from the live stream?',
    a: 'Match the model to the job. Soul models are portrait specialists. Cinematic models are better for wide environments. Nano Banana is useful for structured reference sheets and tiny-character/wide-scene conditioning. Prompt tweaking cannot fix the wrong model choice.',
  },
  {
    q: 'What is the Super Bowl ad process?',
    a: 'It is the creative intelligence layer: Brief, Big Idea, Ideation, Treatment, and Storyboard. Instead of generating random clips, the agent first defines the emotional truth, tests the concept, creates panel-by-panel shots, and only then asks Higgsfield to produce assets.',
  },
  {
    q: 'Can I use this on a non-marketing RoofClaw?',
    a: 'The repo is explicit: hard reject non-marketing souls. If the local SOUL.md is not primarily marketing, advertising, revenue, brand, or content focused, the agent should stop rather than become five confused agents in one box.',
  },
];

const HOW_TO_STEPS = [
  'Confirm the RoofClaw is MARKETING-configured and allowed to operate brand/ad workflows.',
  'Clone the GitHub launchpad and read LAUNCHPAD.md, AGENTS.md, and the safety docs before touching credentials.',
  'Connect Higgsfield MCP for image and video generation, then run a model-selection preflight before generating assets.',
  'Use the Super Bowl creative process to write the brief, big idea, treatment, storyboard, and panel prompts.',
  'Generate image references, character sheets, and videos with strict iteration ceilings and saved asset IDs.',
  'Connect Meta Ads MCP for account context and the official Meta Ads CLI for repeatable execution.',
  'Create campaigns, ad sets, creatives, and ads in PAUSED state only, then hand the final links to a human for review.',
];

const STORY_TO_PAUSED_STEPS = [
  {
    title: 'Story',
    body: 'Start with the offer, audience, pain, and brand voice. If the story is weak, the rest of the workflow only makes weak work faster.',
  },
  {
    title: 'Storyboard',
    body: 'Turn the idea into panel-by-panel shots, copy beats, camera direction, and generation prompts before any model starts spending credits.',
  },
  {
    title: 'Images',
    body: 'Use Higgsfield for key frames, character anchors, scene visuals, reference sheets, and the visual building blocks the video model needs.',
  },
  {
    title: 'Video',
    body: 'Move selected frames into motion, usually through Seedance for production clips, with continuity references instead of one-line prompts.',
  },
  {
    title: 'Tune',
    body: 'Extract thumbnails, trim weak frames, upscale, caption, and prepare the final asset so the ad is usable in the real world.',
  },
  {
    title: 'Page post',
    body: 'Optionally publish organically first so the asset can gather warm-up signals and be inspected in a normal social context.',
  },
  {
    title: 'Paused ad campaign',
    body: 'Create the campaign, ad set, creative, and ad in Meta — but leave every money-moving object paused until a human approves it.',
  },
];

const INSTALL_STEPS = [
  {
    title: 'Confirm the agent fit',
    body: 'The RoofClaw must be MARKETING-configured and allowed to operate brand, advertising, and revenue workflows.',
  },
  {
    title: 'Read the launchpad before credentials',
    body: 'Clone the GitHub repo, then read LAUNCHPAD.md, AGENTS.md, and the safety docs before touching accounts or API keys.',
  },
  {
    title: 'Connect Higgsfield MCP',
    body: 'Run a model-selection preflight before generating assets so the agent chooses the right model for the shot.',
  },
  {
    title: 'Run the creative process',
    body: 'Write the brief, big idea, treatment, storyboard, and panel prompts before spending generation credits.',
  },
  {
    title: 'Generate with ceilings',
    body: 'Create image references, character sheets, and videos with saved asset IDs and strict iteration limits.',
  },
  {
    title: 'Separate context from execution',
    body: 'Use Meta Ads MCP for account context and the official Meta Ads CLI for repeatable campaign operations.',
  },
  {
    title: 'Build paused, then hand off',
    body: 'Create campaigns, ad sets, creatives, and ads in PAUSED state only, then hand the review links to a human.',
  },
];

const StructuredData = () => {
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQ_ITEMS.map(({ q, a }) => ({
      '@type': 'Question',
      name: q,
      acceptedAnswer: { '@type': 'Answer', text: a },
    })),
  };

  const howToSchema = {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name: 'How to turn a marketing RoofClaw into a paused Meta Ads production agent',
    description: 'A safety-first OpenClaw workflow for combining Higgsfield MCP, Meta Ads MCP, Meta Ads CLI, and a story-first creative process.',
    totalTime: 'PT5H',
    supply: [
      { '@type': 'HowToSupply', name: 'Marketing-configured RoofClaw / OpenClaw agent' },
      { '@type': 'HowToSupply', name: 'Higgsfield account with MCP access' },
      { '@type': 'HowToSupply', name: 'Meta Business account and ad account' },
      { '@type': 'HowToSupply', name: 'RoofClaw Meta Ads Agent GitHub launchpad' },
    ],
    tool: [
      { '@type': 'HowToTool', name: 'OpenClaw' },
      { '@type': 'HowToTool', name: 'Higgsfield MCP' },
      { '@type': 'HowToTool', name: 'Meta Ads MCP' },
      { '@type': 'HowToTool', name: 'Meta Ads CLI' },
    ],
    step: HOW_TO_STEPS.map((step, index) => ({
      '@type': 'HowToStep',
      position: index + 1,
      name: step.split(' ').slice(0, 8).join(' '),
      text: step,
    })),
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: SITE },
      { '@type': 'ListItem', position: 2, name: 'Blog', item: `${SITE}/blog` },
      { '@type': 'ListItem', position: 3, name: 'RoofClaw Meta Ads Agent', item: `${SITE}${SLUG}` },
    ],
  };

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(howToSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />
    </>
  );
};

const ArticleImage = ({ src, alt, caption }) => (
  <figure className="not-prose my-14 rounded-[2rem] overflow-hidden border border-[#F0EFF4]/10 bg-[#18181B] shadow-[0_0_40px_rgba(123,97,255,0.12)]">
    <img src={src} alt={alt} className="w-full h-auto mix-blend-screen opacity-95" />
    <figcaption className="px-6 py-4 text-sm text-[#F0EFF4]/45 font-data border-t border-[#F0EFF4]/10">
      {caption}
    </figcaption>
  </figure>
);

const RepoCTA = ({ compact = false }) => (
  <div className={`not-prose ${compact ? 'my-10' : 'my-16'} rounded-[2rem] border border-[#7B61FF]/30 bg-[#18181B] p-8 md:p-10 relative overflow-hidden`}>
    <div className="absolute inset-0 bg-gradient-to-br from-[#7B61FF]/15 via-transparent to-[#10B981]/10" />
    <div className="relative z-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
      <div>
        <p className="text-xs font-data uppercase tracking-[0.25em] text-[#10B981] mb-3">Open-source launchpad</p>
        <h3 className="text-2xl md:text-3xl font-heading font-bold text-[#F0EFF4] mb-3">Build the Meta Ads Agent from the repo.</h3>
        <p className="text-[#F0EFF4]/60 leading-relaxed max-w-2xl">
          Start with <code className="font-data text-[#7B61FF] bg-[#0A0A14] px-2 py-1 rounded">LAUNCHPAD.md</code>. The repo is written for OpenClaw agents, with safety rails, creative process docs, Higgsfield lessons, Meta CLI gotchas, and paused-by-default execution.
        </p>
      </div>
      <a
        href={repoUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center justify-center rounded-full bg-[#7B61FF] px-7 py-4 font-bold text-[#F0EFF4] shadow-[0_0_24px_rgba(123,97,255,0.35)] hover:bg-[#8D78FF] transition-colors whitespace-nowrap"
      >
        Open GitHub Repo ↗
      </a>
    </div>
  </div>
);

const InsightGrid = () => (
  <div className="not-prose my-12 grid md:grid-cols-2 gap-5">
    {[
      ['5 hours', 'Live production time from creative process through real paused Meta ad draft.'],
      ['$13', 'Approximate Higgsfield credit spend across two sessions of casting, frames, and production testing.'],
      ['28 images', 'The working sweet spot for training a consistent Soul ID character.'],
      ['PAUSED', 'The only acceptable default for anything that can spend real ad budget.'],
    ].map(([stat, body]) => (
      <div key={stat} className="rounded-2xl bg-[#18181B] border border-[#F0EFF4]/10 p-6">
        <div className="text-4xl font-heading font-bold text-[#10B981] mb-3">{stat}</div>
        <p className="text-[#F0EFF4]/60 leading-relaxed m-0">{body}</p>
      </div>
    ))}
  </div>
);

const CodeBlock = ({ children }) => (
  <pre className="not-prose my-8 overflow-x-auto rounded-2xl border border-[#F0EFF4]/10 bg-[#05050A] p-6 text-sm text-[#F0EFF4]/75 font-data leading-relaxed">
    <code>{children}</code>
  </pre>
);

const MetaAdsMcpHiggsfieldOpenClaw = () => {
  return (
    <BlogLayout
      title="The Night RoofClaw Became a Meta Ads Studio: Higgsfield MCP, OpenClaw, Meta Ads MCP, the CLI, and the Super Bowl Process"
      date="2026-05-01"
      readTime="22"
      heroImage="/images/blog/meta-ads-mcp-higgsfield-openclaw-hero.jpg"
      canonicalPath={SLUG}
      keywords="Higgsfield MCP, OpenClaw, Meta Ads MCP, Meta Ads CLI, roofing marketing AI, RoofClaw Meta Ads Agent, AI video ads for roofers, Seedance 2.0, Soul ID, Super Bowl ad process, Facebook ads for roofing companies"
      description="A field report from a live RoofClaw production session: how OpenClaw, Higgsfield MCP, Meta Ads MCP, and the official Meta Ads CLI turned a roofing brand-film process into a paused Meta Ads launchpad."
      listItems={HOW_TO_STEPS}
    >
      <StructuredData />

      <div className="not-prose article-summary my-10 rounded-2xl border border-[#7B61FF]/30 bg-[#18181B] p-7">
        <p className="text-xs font-data uppercase tracking-[0.25em] text-[#7B61FF] mb-4">Field report</p>
        <p className="text-xl leading-relaxed text-[#F0EFF4]/80 m-0">
          This is what happened when a marketing-configured RoofClaw was pointed at the new generation of ad tools: Higgsfield MCP for cinematic assets, Meta Ads MCP for account context, Meta Ads CLI for deterministic execution, and a Super Bowl-grade creative process for keeping the work from becoming random AI slop.
        </p>
      </div>

      <RepoCTA compact />

      <p>
        The dangerous way to use AI advertising tools is to open a generator, type "make me a roofing ad," and hope the machine accidentally finds a story. That is how you burn credits, create off-brand clips, and hand Meta a forgettable creative with no reason to win the auction.
      </p>

      <p>
        The useful way is slower at the beginning and much faster by the end. You treat the agent like a creative department with operating procedures. It reads the brand. It defines the customer. It writes the brief. It chooses the model. It generates assets under a cost ceiling. It moves the final asset into Meta with every money-moving object paused for approval.
      </p>

      <p>
        That is what the live stream proved. Not that one tool can make a cool video. Everyone can make a cool video now. The proof was that an OpenClaw agent could coordinate a production stack: story, storyboard, character casting, Higgsfield model selection, video generation, Meta account inspection, campaign creation, ad-set creation, creative creation, and final ad draft — without pretending that automation means removing human judgment.
      </p>

      <VideoEmbed
        youtubeId="wd9fwVmoglc"
        title="The Adam Sand Report: live RoofClaw Meta Ads Agent build session"
      />

      <Callout eyebrow="Trust rule" title="No ad spend moves without human approval." variant="amber">
        <p>
          The Meta Ads Agent can prepare the campaign, organize the creative, and build the launchpad. It does not get to spend money by itself. Campaigns, ad sets, creatives, and ads are created paused by default, then handed to a human for final review.
        </p>
      </Callout>

      <InsightGrid />

      <h2>The thesis: creative agents are not prompt boxes. They are operating systems.</h2>

      <p>
        The market is about to misunderstand AI ads in the same way it misunderstood chatbots. The shallow version is a box that writes copy. The serious version is an agent that carries context across tools, remembers what failed, knows which model fits which shot, respects spend controls, and can hand a human a complete paused campaign instead of a folder full of disconnected assets.
      </p>

      <p>
        RoofClaw is built for that second version. Because it runs on OpenClaw, it is not trapped in one vendor's interface. It can read a GitHub launchpad, call an MCP server, run a CLI, use local files as production memory, generate images, coordinate sub-agents, and leave behind a repo-quality audit trail. The point is not "AI made an ad." The point is that the work became teachable, repeatable, and safer every time the agent touched a real constraint.
      </p>

      <Takeaway
        title="Operator takeaway"
        items={[
          'A prompt box gives you an output. An operating system gives you a repeatable production path.',
          'The moat is not access to models. The moat is saved judgment, reusable process, and safe execution.',
        ]}
      />

      <ArticleImage
        src="/images/blog/meta-ads-mcp-live-pipeline.jpg"
        alt="Diagram of the RoofClaw Meta Ads live-stream pipeline: brief, storyboard, Higgsfield, tune, Meta campaign"
        caption="The live-stream pipeline: story first, generation second, platform execution last — with spend controls locked to PAUSED."
      />

      <h2>What launched: a GitHub repo that turns lessons into an installable agent path</h2>

      <p>
        The GitHub project is not a toy prompt pack. It is a launchpad for a qualifying RoofClaw. The intended handoff is simple: point a marketing-configured RoofClaw at the repo and tell it to begin with <code>LAUNCHPAD.md</code>. From there, the agent reads the safety rules, validates its own identity, connects the right tools, and follows the seven-step workflow.
      </p>

      <p>
        The repo is strict on purpose. It tells the agent to hard-reject non-marketing souls. A Chief of Staff RoofClaw should not suddenly become an ad buyer. A support agent should not start touching brand creative. A production coordinator should not be trusted to write campaign copy just because it can run a CLI. Specialization matters more as agents gain write access.
      </p>

      <p>
        That one rule protects the client. It prevents the common failure mode where a single agent becomes a vague generalist with too many permissions and no taste. Marketing work requires audience empathy, brand sense, offer clarity, and restraint around spend. If the local <code>SOUL.md</code> is not primarily marketing, advertising, revenue, brand, or content focused, the agent stops.
      </p>

      <Callout eyebrow="Install gate" title="Wrong soul, wrong tool." variant="red">
        <p>
          A Chief of Staff, support, operations, or production RoofClaw should reject this install. Meta creative work belongs to a marketing or revenue agent with brand judgment and explicit spend guardrails.
        </p>
      </Callout>

      <h2>The seven-step flow: from story to paused campaign</h2>

      <p>
        The workflow is intentionally boring in the places where boring saves money. It does not begin with a Meta campaign. It begins with the story. It does not begin with a Higgsfield render. It begins with a storyboard. It does not end with an active ad. It ends with a paused ad ready for human approval.
      </p>

      <StepCardList
        title="Seven steps"
        intro="This is the part a returning visitor should be able to find in seconds. Story first. Platform execution last. Spend controls locked down the whole way."
        steps={STORY_TO_PAUSED_STEPS}
      />

      <p>
        The genius is not in any single step. The genius is that each step produces an artifact the next step can inspect. The brief becomes the big idea. The big idea becomes the treatment. The treatment becomes storyboard panels. The panels become Higgsfield prompts. The selected outputs become video references. The tuned asset becomes Meta creative. The Meta creative becomes a paused ad. Every move leaves a trail.
      </p>

      <h2>The Super Bowl process: why the ad worked before the tools ran</h2>

      <p>
        The phrase "Super Bowl ad process" can sound theatrical until you understand what it prevents. It prevents feature-first ads. It prevents random montage videos. It prevents the agent from confusing production value with persuasion. And, most importantly, it forces a roofing business to ask: what is the human truth here?
      </p>

      <p>
        For the brand-film demo, the answer was not "roofers need software." Nobody cries over software. The human truth was that roofers do not quit. They work in brutal heat, answer the phone after hours, carry the weight of families and payroll, and still show up when the weather turns ugly. The brand truth was that RoofClaw exists to carry the operational weight that keeps good roofers chained to the desk after the job is done.
      </p>

      <blockquote>
        Human Truth + Brand Truth = Big Idea. If the agent cannot write that equation in plain English, it has no business generating a frame.
      </blockquote>

      <p>
        This is why the repo documents the creative process as a first-class skill. It tells the agent to move through Brief, Big Idea, Ideation, Treatment, and Storyboard. It includes kill criteria. It includes concept formats. It includes the Keyframe Test: one image plus ten words or fewer that communicate the whole story. If the keyframe fails, the ad is not ready for Higgsfield.
      </p>

      <Takeaway
        title="Super Bowl process in plain English"
        items={[
          'Brief: define the audience, offer, and business constraint.',
          'Big Idea: state the human truth plus the brand truth.',
          'Ideation: generate concepts, then kill the weak ones early.',
          'Treatment: choose the emotional arc and visual approach.',
          'Storyboard: turn the idea into concrete shots before generation.',
        ]}
      />

      <ArticleImage
        src="/images/blog/super-bowl-creative-process-pipeline.jpg"
        alt="Diagram of the Super Bowl creative process: brief, big idea, concepts, treatment, storyboard"
        caption="The creative layer: the agent earns the right to generate only after the story survives the brief, big idea, treatment, and Keyframe Test."
      />

      <h2>Higgsfield MCP: the beautiful machine that punishes lazy model selection</h2>

      <p>
        Higgsfield was the cinematic engine in the workflow. It handled reference sheets, character identity, production frames, and video. But the live session exposed the most important lesson for OpenClaw users: you do not select a Higgsfield model by reading the marketing name. You select by shot intent.
      </p>

      <p>
        Soul models are excellent when the job is a person. Face, expression, emotional close-up, identity lock — that is where they shine. But ask a Soul model for an extreme wide drone shot with a tiny roofer visible through a window and it will fight you. It wants a portrait. It will crop, drift, or ignore the wide framing. Prompt adjectives cannot change the model's center of gravity.
      </p>

      <p>
        The model matrix from the session became simple: use <code>cinematic_studio_2_5</code> for wide environmental establishers, <code>nano_banana_2</code> for structured reference sheets and wide shots with tiny figures, <code>soul_2</code> with a trained <code>soul_id</code> for intimate character closeups, <code>soul_cinematic</code> for dramatic portraits, and <code>marketing_studio_image</code> for commercial UGC-style product or service visuals.
      </p>

      <Takeaway
        title="Higgsfield model-selection cheat sheet"
        variant="violet"
        items={[
          'Wide environment: use cinematic_studio_2_5.',
          'Structured reference sheet or tiny figure in a wide shot: use nano_banana_2.',
          'Close-up character identity: use soul_2 with a trained soul_id.',
          'Dramatic portrait: use soul_cinematic.',
          'Commercial UGC/service visual: use marketing_studio_image.',
        ]}
      />

      <ArticleImage
        src="/images/blog/higgsfield-model-selection-matrix.jpg"
        alt="Higgsfield model selection matrix for wide shots, character closeups, dramatic portraits, and commercial UGC ads"
        caption="The cardinal rule: choose the Higgsfield model by job type. The wrong model will silently override the brief."
      />

      <h3>The 28-image character casting method</h3>

      <p>
        The live stream also proved a practical casting pipeline. Before asking for production shots, the agent creates a single character reference sheet, then produces 28 reference variations. The distribution matters: studio identity locks, real-world contexts, expression studies, and lighting variations. Those references become the training set for Soul ID.
      </p>

      <p>
        Why 28? Because the Higgsfield docs ask for 20+, but production showed that 28 is a useful sweet spot. It gives the model enough face, body, wardrobe, expression, lighting, and environment variation without turning the training set into noise. In the session, this was used to cast multiple roofer archetypes for the brand film.
      </p>

      <Checklist
        title="Higgsfield character preflight"
        items={[
          'Generate a four-angle character sheet before any scene work.',
          'Create references in four groups: identity, context, expression, lighting.',
          'Upload and save every media ID instead of relying on the recent-generation feed.',
          'Train Soul ID through the web UI when MCP cannot expose the training step.',
          'Store the Soul ID UUID in project state immediately after the human provides it.',
          'Use 4–6 relevant anchors per shot, not all 28 images at once.',
        ]}
      />

      <h3>Iteration discipline: stop at V5</h3>

      <p>
        Another expensive lesson: there is a point where more rerolls stop improving the asset. Versions one through three often sharpen the idea. Version four might solve one visible problem. By version five, the model is usually telling you something. Either the model is wrong, the prompt is asking for conflicting physics, or the shot should be fixed in post.
      </p>

      <p>
        This is where agent memory becomes valuable. A human may keep trying because the next render could be the one. A disciplined RoofClaw should stop, inspect the failure class, and choose: switch models, change references, accept the frame and comp the defect, or rewrite the panel. That decision can save hundreds of credits over a month of production.
      </p>

      <Callout eyebrow="Iteration ceiling" title="V5 is a decision point, not a dare." variant="amber">
        <p>
          If version five still misses, the agent should stop rerolling and diagnose the failure: wrong model, wrong references, impossible shot, or a panel that belongs in post-production instead of generation.
        </p>
      </Callout>

      <h2>Seedance 2.0: video generation needs continuity, not one-line prompts</h2>

      <p>
        Seedance is powerful, but the session notes make one thing clear: it rewards structured direction. A one-line prompt gives the model permission to improvise. A multi-shot prompt gives it camera movement, subject action, lighting, transition logic, and emotional pacing. For a commercial, that difference is not cosmetic. It is the difference between a usable clip and a pretty accident.
      </p>

      <p>
        The production default is <code>seedance_2_0</code>, not the fast variant, for anything intended to ship. Draft mode has a place, but a final ad should not be built from the lowest-effort path. The session also documented the 15-second default: generate the maximum usable clip length, then edit down. More source material creates more flexibility in the final cut.
      </p>

      <p>
        The continuity trick matters most for multi-scene work. Do not feed the next scene a screenshot of the last frame. Feed it the previous finished clip as a video reference. A screenshot carries composition. A clip carries motion, camera speed, lighting change, and energy. This is the kind of practical detail an agent should remember forever.
      </p>

      <CodeBlock>{`Seedance production default:
- model: seedance_2_0
- duration: 15 seconds when supported
- aspect ratio: choose before generation, never fix by cropping later
- references: character sheet + key visual + previous clip when continuing
- prompt: multi-shot, with camera movement and action beats
- post: upscale before editing and final delivery`}</CodeBlock>

      <h2>Meta Ads MCP and the CLI: two tools, two jobs</h2>

      <p>
        The Meta side of the system showed why OpenClaw is a strong home for this kind of work. MCP and CLI are not competitors. They are lanes. The MCP is the natural-language reasoning layer. The CLI is the deterministic execution layer.
      </p>

      <p>
        Meta Ads MCP is useful when the agent needs to understand the account: what campaigns exist, what assets are available, what pages or ad accounts are connected, and what the next safe action might be. It is conversational. It gives the agent context.
      </p>

      <p>
        The official Meta Ads CLI is useful when the agent needs repeatability. Commands can be logged, copied, reviewed, retried, wrapped, and tested. For OpenClaw users, that matters because the goal is not a magical one-off. The goal is a launchpad that another marketing RoofClaw can read and execute safely.
      </p>

      <Takeaway
        title="MCP vs. CLI"
        items={[
          'Use MCP for account context, reasoning, inspection, and conversational exploration.',
          'Use the CLI for repeatable execution, logs, retries, scripts, and reviewable commands.',
          'Use a small wrapper when Meta video creative payloads require details the CLI cannot express cleanly yet.',
        ]}
      />

      <ArticleImage
        src="/images/blog/meta-mcp-cli-execution-stack.jpg"
        alt="Diagram showing Meta Ads MCP for context, Meta Ads CLI for execution, and a Python wrapper for API gaps"
        caption="MCP answers the account-context questions. CLI handles repeatable execution. A small wrapper covers gaps the CLI cannot yet handle cleanly."
      />

      <h3>The real CLI gotchas that only appear under production pressure</h3>

      <p>
        The session notes are valuable because they are not theoretical. They list the specific things that broke or nearly broke when a real agent touched a real ad account. New Meta apps default to development mode, which can allow read endpoints while silently blocking creative creation. Budgets are in cents, not dollars. Campaign Budget Optimization conflicts with ad-set budgets if you do not choose the structure intentionally. Large accounts can make unbounded list commands hang.
      </p>

      <p>
        The video creative path was the biggest trap. The CLI help text suggests one thing, but production showed that video creatives need a thumbnail hash and the official CLI path could not attach the right payload cleanly. The repo now includes a wrapper pattern that uploads the video, uploads the thumbnail, polls until Meta finishes processing, and assembles the correct creative structure.
      </p>

      <Checklist
        title="Meta execution safety checklist"
        items={[
          'Confirm the Meta app mode supports creative creation before testing writes.',
          'Write budgets in cents and comment the dollar equivalent beside every value.',
          'Choose campaign-level CBO or ad-set-level budgets; never accidentally set both.',
          'Always pass limits to list commands on mature accounts.',
          'Use the wrapper path for video creatives that need thumbnail hashes and CTA links.',
          'Poll video processing status before referencing the uploaded video in a creative.',
          'Create every campaign, ad set, creative, and ad in PAUSED state by default.',
        ]}
      />

      <h2>The OpenClaw lesson: agents need state, files, and sub-agents</h2>

      <p>
        A browser-only assistant can help you write an ad. An OpenClaw agent can run the production. That is the difference. The workflow required files for persistent project state, GitHub for source-controlled process docs, MCP servers for external capabilities, CLI commands for deterministic writes, and sub-agents for parallelizable work like character casting.
      </p>

      <p>
        Multi-character casting is an obvious example. Three characters times 28 references each equals 84 independent generations. Run that sequentially and you waste the evening. Run it through sub-agents with clear output directories, cost ceilings, and success criteria, and the parent agent can coordinate while the child agents produce assets in parallel.
      </p>

      <p>
        That only works if the agent is disciplined about state. The live stream produced the world bible pattern: a single JSON file that holds character descriptions, wardrobe rules, vehicles, locations, lighting palettes, frame anchors, model decisions, media IDs, and global negative prompts. When the wardrobe changes, it changes once. When the vehicle needs a strict constraint, it lives in one place. Drift becomes less likely because prompts compose from a shared source of truth.
      </p>

      <CodeBlock>{`World-bible pattern:
{
  "characters": {
    "Marcus": {
      "status": "cast_and_trained",
      "soul_id_v2": "<uuid>",
      "wardrobe_rule": "no logos, no brand marks, no text",
      "media_ids_file": "./casting/marcus/media_ids.json"
    }
  },
  "vehicles": {
    "work_truck": "white single-cab roofing truck, no logo, no crew cab"
  },
  "lighting_palettes": {
    "pre_dawn": { "key_phrase": "cold blue worklight before sunrise" }
  },
  "global_directives": {
    "color_grade_rule": "cinematic but grounded, no plastic AI sheen"
  }
}`}</CodeBlock>

      <h2>For roofers: this is not about making pretty ads. It is about speed to market.</h2>

      <p>
        A roofing company does not need a Cannes film every Tuesday. It needs speed to market when weather creates demand. It needs a storm offer turned into creative while the phones are hot. It needs brand consistency without waiting on a freelancer. It needs an agent that can move from idea to paused campaign fast enough that the owner can approve the thing before the opportunity goes cold.
      </p>

      <p>
        That is why the process matters more than the individual output. The agent can build a storm-damage inspection campaign, a financing campaign, a metal-roofing education campaign, a hiring campaign, a referral campaign, or a brand-film campaign. The same spine applies: brief, story, storyboard, generate, tune, publish, advertise, pause for approval.
      </p>

      <p>
        The best roofing marketers already know the creative angle. They know what homeowners fear after a hailstorm. They know what a good project manager saves. They know what an insurance supplement conversation feels like. RoofClaw gives that knowledge a production arm. It turns the marketer's judgment into assets, files, commands, and draft campaigns.
      </p>

      <Takeaway
        title="Why this matters for a roofer"
        variant="amber"
        items={[
          'Storm demand has a shelf life. A campaign approved tomorrow may miss the money today.',
          'The agent does the production grind so the owner can spend judgment on offer, budget, and timing.',
          'The result is not an auto-spending ad bot. It is a faster path to a reviewable campaign.',
        ]}
      />

      <h2>How to install the thinking, not just the tools</h2>

      <p>
        Tools are the easy part. The hard part is teaching the agent when to stop, when to switch models, when to reject an install, and when to hand control back to a human. That is why the GitHub project includes more than setup instructions. It includes creative docs, lessons learned, gotchas, templates, and safety rules.
      </p>

      <p>
        If you are an OpenClaw user, the pattern is worth copying even outside Meta ads. Put the process in the repo. Put hard rules in <code>AGENTS.md</code>. Put the workflow in a named document. Put gotchas in a doc that future agents must read before execution. Put templates in files. Put state in JSON. Make the agent inspect its identity before touching tools that can harm the business.
      </p>

      <h3>Practical setup path</h3>

      <StepCardList
        title="Visible install path"
        intro="The schema already had a how-to. This renders the same practical structure for humans so the article is useful, not just machine-readable."
        steps={INSTALL_STEPS}
        variant="green"
      />

      <RepoCTA />

      <h2>The bigger picture: advertising agents are becoming operators</h2>

      <p>
        The old ad stack separated strategy, creative, production, media buying, reporting, and CRM follow-up into different people and tools. That made sense when software could not move across boundaries. But agents change the shape of work. A properly-scoped marketing agent can carry context from the offer to the creative to the platform to the report.
      </p>

      <p>
        That does not eliminate humans. It changes where humans should stand. The human should judge the offer, approve the creative, check the budget, and decide whether the message represents the company. The agent should do the mechanical work around that judgment: create versions, enforce naming, prepare assets, run checks, document gotchas, build paused drafts, and make the next run easier.
      </p>

      <p>
        This is why the live stream was bigger than a demo. It showed the shape of the next roofing marketing department: a small number of sharp humans, surrounded by specialized local agents that can produce, execute, and remember. Not SaaS dashboards. Not prompt toys. Actual operators.
      </p>

      <h2>FAQ</h2>

      <div className="not-prose my-10 space-y-4">
        {FAQ_ITEMS.map(({ q, a }) => (
          <details key={q} className="rounded-2xl border border-[#F0EFF4]/10 bg-[#18181B] p-6">
            <summary className="cursor-pointer font-heading text-lg font-bold text-[#F0EFF4]">{q}</summary>
            <p className="mt-4 text-[#F0EFF4]/65 leading-relaxed mb-0">{a}</p>
          </details>
        ))}
      </div>

      <h2>The final word</h2>

      <p>
        The lesson of the night was simple: access to tools is not the moat. Discipline is the moat. The agent that wins is not the one with the fanciest prompt. It is the one that knows when the model is wrong, when the story is weak, when the budget could be misread, when the CLI needs a wrapper, and when a human needs to approve the next move.
      </p>

      <p>
        That is what we are putting into the RoofClaw Meta Ads Agent repo. Not just the commands. The judgment. The scars. The things we learned because we actually ran the workflow live and wrote down what broke.
      </p>

      <RepoCTA compact />
    </BlogLayout>
  );
};

export default MetaAdsMcpHiggsfieldOpenClaw;
